import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { NavigateFunction } from "react-router-dom";
import { updateMedicalPaOrder } from "../../../api/firebase/firestoreService";
import { updateNchQuestionnaire } from "../../../api/patchCall/updateNchQuestionnaire";
import { medicalPaSubmission } from "../../../api/postCall/medicalPaSubmission";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { MedicalQuestion } from "../../../data-model/medicalQuestion";
import { WorkflowConfig } from "../../../data-model/workflowConfig";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

/**
 * Redirects the user to their originating worklist after a successful
 * questionnaire submission, falling back to the root worklist.
 *
 * @param navigate - React Router navigation function.
 */
export const navigateAfterSubmit = (navigate: NavigateFunction, id: string) => {
  navigate(`/worklists/submission-status/${id}`);
};

/**
 * Triggers a document save on the medical PA order by setting the
 * `save_trigger` flag in Firestore.
 *
 * @param orderId - The medical PA order identifier.
 * @throws Re-throws the original error after logging.
 */
export const updateOrderDocuments = async (orderId: string) => {
  try {
    await updateMedicalPaOrder(orderId, {
      "status.save_trigger": "true",
    });
  } catch (error) {
    logError(error as Error, "Error updating order documents");
    throw error;
  }
};

/**
 * Persists questionnaire answers via the NCH API and updates the
 * associated order documents. Shows a success or error toast.
 *
 * @param id - The medical PA order identifier.
 * @param questionnaireAnswers - The answered question tree to persist.
 * @throws Re-throws so the orchestrator can halt the pipeline.
 */
const saveQuestionnaire = async (
  id: string,
  questionnaireAnswers: MedicalQuestion[],
) => {
  try {
    await updateNchQuestionnaire({
      identifier: id,
      question_tree: questionnaireAnswers,
    });
    await updateOrderDocuments(id);
    toast.success(ToastMessages.QUESTIONNAIRE_UPDATED_SUCCESS);
  } catch (error) {
    logError(error as Error, "Error updating questionnaire");
    toast.error(ToastMessages.QUESTIONNAIRE_UPDATED_ERROR);
    throw error;
  }
};

/**
 * Submits the medical PA order for processing, routing through the
 * legacy endpoint when the workflow's submission type requires it.
 *
 * @param id - The medical PA order identifier.
 * @param workflowConfig - Current workflow configuration used to
 *   determine whether the legacy submission path applies.
 * @throws Re-throws so the orchestrator can halt the pipeline.
 */
const submitOrder = async (id: string, workflowConfig: WorkflowConfig) => {
  try {
    await medicalPaSubmission(
      id ?? "",
      getOrgIdForFetchExternalWorklist(),
      undefined,
    );
    toast.success(ToastMessages.SUBMISSION_INITIATED_SUCCESS);
  } catch (error) {
    logError(error as Error, "Error initiating submission");
    toast.error(ToastMessages.SUBMISSION_INITIATED_FAILURE);
    throw error;
  }
};

/**
 * Orchestrates the full questionnaire submission flow:
 * 1. Save questionnaire answers and update order documents.
 * 2. Submit the PA order for processing.
 * 3. Navigate back to the worklist on success.
 *
 * Each step handles its own error logging and user-facing toasts.
 * If any step fails the pipeline stops and the user stays on page.
 *
 * @param navigate - React Router navigation function.
 * @param id - The medical PA order identifier.
 * @param questionnaireAnswers - The answered question tree to persist.
 * @param isDirty - Whether the user has modified any answers.
 * @param setSubmitLoading - Callback to toggle the loading indicator.
 * @param workflowConfig - Current workflow configuration.
 */
export const handleQuestionnaireSubmit = async (
  navigate: NavigateFunction,
  id: string,
  questionnaireAnswers: MedicalQuestion[],
  setSubmitLoading: (loading: boolean) => void,
  workflowConfig: WorkflowConfig,
) => {
  setSubmitLoading(true);
  try {
    await saveQuestionnaire(id, questionnaireAnswers);
    await submitOrder(id, workflowConfig);
    navigateAfterSubmit(navigate, id);
  } catch {
    // individual steps already handle logging and toasts
  } finally {
    setSubmitLoading(false);
  }
};
